import { Trash2 } from "lucide-react";
import React from "react";

import type { MedVisitDetailResponse } from "../../../types/medchron";
import { HighlightText } from "../HighlightText";
import type { EditingVisitDetailState, MedChronTemplate, VisitDisplay } from "./types";
import { areVisitDetailEditsSemanticallyNoop } from "./utils/medVisitDetailNormalize";
import { formatDetailCategory } from "./utils/visitFormatting";

interface VisitDetailRowProps {
  detail: MedVisitDetailResponse;
  visit: VisitDisplay;
  editMode: boolean;
  editingVisitDetail: EditingVisitDetailState | null;
  setEditingVisitDetail: (v: EditingVisitDetailState | null) => void;
  template: MedChronTemplate | null;
  handleVisitDetailUpdate: (
    visitId: string,
    oldCategory: string,
    newCategory: string,
    newDetails: string
  ) => void;
  onDeleteDetail: (versionId: string, visitId: string, category: string) => void;
  isSavingDetail: boolean;
  autoResizeTextarea: (el: HTMLTextAreaElement | null) => void;
  highlightQuery: string;
}

export const VisitDetailRow: React.FC<VisitDetailRowProps> = ({
  detail,
  visit,
  editMode,
  editingVisitDetail,
  setEditingVisitDetail,
  template,
  handleVisitDetailUpdate,
  onDeleteDetail,
  isSavingDetail,
  autoResizeTextarea,
  highlightQuery,
}) => {
  const isEditing =
    editingVisitDetail?.visitId === visit.visit_id &&
    editingVisitDetail?.category === detail.visit_detail_category;

  if (isEditing && editingVisitDetail) {
    const hasVisitDetailChanges = !areVisitDetailEditsSemanticallyNoop(
      detail.visit_detail_category,
      detail.visit_details,
      editingVisitDetail.newCategory,
      editingVisitDetail.details
    );

    return (
      <div
        data-visit-card-no-bulk-select
        className="rounded-xl border border-neutral-200 bg-card p-4 shadow-sm"
      >
        <input
          type="text"
          placeholder="Category"
          value={editingVisitDetail.newCategory}
          onChange={(e) =>
            setEditingVisitDetail({ ...editingVisitDetail, newCategory: e.target.value })
          }
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              if (hasVisitDetailChanges) {
                handleVisitDetailUpdate(
                  visit.visit_id,
                  detail.visit_detail_category,
                  editingVisitDetail.newCategory,
                  editingVisitDetail.details
                );
              }
            }
          }}
          className="mb-3 w-full rounded-lg border border-neutral-200 bg-neutral-50/50 px-3 py-2.5 text-sm transition-colors focus-visible:bg-card focus-visible:shadow-focus-inset"
        />
        <textarea
          placeholder="Details"
          value={editingVisitDetail.details}
          onChange={(e) =>
            setEditingVisitDetail({ ...editingVisitDetail, details: e.target.value })
          }
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              if (hasVisitDetailChanges) {
                handleVisitDetailUpdate(
                  visit.visit_id,
                  detail.visit_detail_category,
                  editingVisitDetail.newCategory,
                  editingVisitDetail.details
                );
              }
            } else if (e.key === "Enter" && e.shiftKey) {
              setEditingVisitDetail({
                ...editingVisitDetail,
                details: editingVisitDetail.details + "\n",
              });
            }
          }}
          ref={(el) => autoResizeTextarea(el)}
          className="mb-0 w-full rounded-lg border border-neutral-200 bg-neutral-50/50 px-3 py-2.5 text-sm transition-colors focus-visible:bg-card focus-visible:shadow-focus-inset"
          rows={3}
        />
        <div className="mt-4 flex flex-wrap items-center justify-end gap-3">
          <button
            type="button"
            onClick={() =>
              handleVisitDetailUpdate(
                visit.visit_id,
                detail.visit_detail_category,
                editingVisitDetail.newCategory,
                editingVisitDetail.details
              )
            }
            disabled={isSavingDetail || !hasVisitDetailChanges}
            title={!hasVisitDetailChanges ? "No changes to save" : undefined}
            className="cursor-pointer rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors duration-200 hover:!bg-primary-hover hover:!text-white focus-visible:shadow-focus disabled:opacity-50"
          >
            {isSavingDetail ? "Saving..." : "Save"}
          </button>
          <button
            type="button"
            onClick={() => setEditingVisitDetail(null)}
            disabled={isSavingDetail}
            className="cursor-pointer rounded-lg border border-neutral-200 bg-card px-4 py-2 text-sm font-medium text-neutral-700 shadow-sm transition-colors duration-200 hover:border-neutral-300 hover:bg-neutral-50 focus-visible:shadow-focus disabled:opacity-50"
          >
            Cancel
          </button>
          {editMode && (
            <button
              type="button"
              aria-label="Delete this detail"
              onClick={() =>
                onDeleteDetail(visit.version_id, visit.visit_id, detail.visit_detail_category)
              }
              className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-neutral-200 bg-card px-3 py-2 text-sm font-medium text-neutral-500 shadow-sm transition-colors duration-200 hover:border-destructive-border hover:bg-destructive-subtle hover:text-destructive-text focus-visible:shadow-focus-danger focus-visible:outline-none"
            >
              <Trash2 size={14} strokeWidth={2.5} />
              Delete
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      data-visit-card-no-bulk-select
      className="group relative rounded-lg border border-neutral-100 bg-neutral-50/30 px-3 py-1 transition-all duration-200 hover:bg-neutral-50/60"
    >
      {editMode && (
        <button
          type="button"
          aria-label="Delete this detail"
          onClick={() =>
            onDeleteDetail(visit.version_id, visit.visit_id, detail.visit_detail_category)
          }
          className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-1.5 text-neutral-400 opacity-0 transition-all hover:bg-destructive-subtle hover:text-destructive-text group-hover:opacity-100"
        >
          <Trash2 size={16} strokeWidth={2.5} />
        </button>
      )}
      <div
        role={editMode ? "button" : undefined}
        tabIndex={editMode ? 0 : undefined}
        onClick={() =>
          editMode &&
          setEditingVisitDetail({
            visitId: visit.visit_id,
            category: detail.visit_detail_category,
            newCategory: detail.visit_detail_category,
            details: detail.visit_details,
          })
        }
        onKeyDown={(e) =>
          editMode &&
          e.key === "Enter" &&
          setEditingVisitDetail({
            visitId: visit.visit_id,
            category: detail.visit_detail_category,
            newCategory: detail.visit_detail_category,
            details: detail.visit_details,
          })
        }
        className={editMode ? "cursor-pointer pr-8" : ""}
      >
        <div className="block">
          <span className="mr-1.5 inline text-xs font-semibold uppercase tracking-micro text-black">
            <HighlightText
              text={formatDetailCategory(template, detail.visit_detail_category, visit.isCollapsed)}
              highlightQuery={highlightQuery}
            />
            :
          </span>
          <span className="whitespace-pre-line text-sm leading-relaxed text-neutral-900">
            <HighlightText text={detail.visit_details} highlightQuery={highlightQuery} />
          </span>
        </div>
      </div>
    </div>
  );
};
